import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { customStyles } from '../../../utils';
import { getRequest, postRequest } from '../../../config';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

const S = {
    wrap: { padding: '1.5rem', background: '#f5f6fa', minHeight: 120 },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e4e8f5', boxShadow: '0 2px 8px rgba(74,103,255,0.06)', overflow: 'hidden' },
    cardHead: { padding: '18px 24px 14px', borderBottom: '1px solid #f0f2ff' },
    title: { fontSize: '1.05rem', fontWeight: 700, color: '#1a1d2e', margin: 0 },
    subtitle: { fontSize: '0.8rem', color: '#7c83a0', marginTop: 4 },
    tableWrap: { overflowX: 'auto', width: '100%' },
    thead: { background: '#f8f9ff' },
    th: { padding: '10px 16px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7c83a0', textAlign: 'left', borderBottom: '2px solid #e8eaf5', whiteSpace: 'nowrap' },
    modRow: { cursor: 'pointer', borderTop: '2px solid #e8eaf5', background: 'linear-gradient(90deg,#f0f3ff 0%,#f8f9ff 100%)', userSelect: 'none' },
    modTd: { padding: '10px 16px' },
    modAccent: { width: 3, height: 34, background: '#4A67FF', borderRadius: 2, flexShrink: 0 },
    modName: { fontSize: '0.88rem', fontWeight: 700, color: '#1a1d2e' },
    modKey: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, flexShrink: 0 },
    modMeta: { fontSize: '0.72rem', color: '#9aa0b8' },
    secRow: { borderBottom: '1px solid #f0f2ff', background: '#fff' },
    secTd: { padding: '9px 12px 9px 32px', width: '40%' },
    secNo: { fontSize: '0.68rem', fontWeight: 700, color: '#4A67FF', background: '#eef0ff', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 },
    secName: { fontSize: '0.82rem', color: '#374151' },
    advisorTd: { padding: '7px 16px 7px 12px', width: '60%' },
};

const useFeaturedSelectStyles = () =>
    useMemo(() => ({
        ...customStyles,
        control: (p, s) => ({
            ...customStyles.control(p, s),
            minHeight: 34,
            fontSize: '13px',
            border: '1px solid #e2e5ef',
            borderRadius: 8,
            backgroundColor: '#fff',
            boxShadow: s.isFocused ? '0 0 0 2px rgba(74,103,255,0.18)' : 'none',
        }),
        valueContainer: (p) => ({ ...customStyles.valueContainer(p), padding: '3px 10px' }),
        indicatorsContainer: (p) => ({ ...p, height: 32 }),
        menu: (p, s) => ({ ...customStyles.menu(p, s), width: 'max(100%,220px)', zIndex: 10021 }),
        menuPortal: (b) => ({ ...b, zIndex: 10021 }),
        singleValue: (p) => ({ ...p, fontSize: '13px', color: '#1a1d2e' }),
        placeholder: (p) => ({ ...customStyles.placeholder(p), fontSize: '13px', color: '#b0b7cc' }),
    }), []);

const TempFeaturedBusinessCards = () => {
    const selectStyles = useFeaturedSelectStyles();
    const [tempModules, setTempModules] = useState([]);
    const [advisorsData, setAdvisorsData] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [modulesRes, advisorsRes, allocRes] = await Promise.all([
                getRequest('/admin/temp-modules-admin', 'not-showMessage'),
                getRequest('/user/getAllMainAdvisors', 'not-showMessage'),
                getRequest('/userAllocation/temp-section', 'not-showMessage'),
            ]);
            if (modulesRes?.code === 200) setTempModules(modulesRes.body || []);
            if (advisorsRes?.code === 200) {
                setAdvisorsData((advisorsRes.body || []).map(a => ({
                    ...a,
                    label: `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim(),
                    value: String(a._id),
                    advisorTypeName: a.advisorType?.name ? `(${a.advisorType.name})` : '',
                })));
            }
            if (allocRes?.code === 200) setAllocations(allocRes.body || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const toggleAccordion = (moduleId) =>
        setOpenAccordions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));

    const getSelectedAdvisor = (sectionNo, moduleId) => {
        const alloc = allocations.find(
            a => a.sectionNo === sectionNo && String(a.moduleId) === String(moduleId)
        );
        if (!alloc) return null;
        return advisorsData.find(a => String(a._id) === String(alloc.advisorId)) || null;
    };

    const handleAllocation = async ({ advisorId, sectionNo, moduleId }) => {
        try {
            const res = await postRequest('/userAllocation/temp-section', { advisorId, sectionNo, moduleId });
            if (res?.code === 200) setAllocations(res.body || []);
        } catch (err) {
            console.error(err);
        }
    };

    const formatOptionLabel = ({ label, advisorTypeName }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ textTransform: 'capitalize', color: '#1a1d2e' }}>{label}</span>
            {advisorTypeName && <span style={{ fontSize: '0.75rem', color: '#7c83a0' }}>{advisorTypeName}</span>}
        </div>
    );

    if (loading) return <div className="df jcc aic p2rem" style={{ color: '#7c83a0' }}>Loading…</div>;

    return (
        <div style={S.wrap}>
            <div style={S.card}>
                <div style={S.cardHead}>
                    <p style={S.title}>Featured Business Cards Management</p>
                    <p style={S.subtitle}>Assign a featured advisor to each module section. Expand a module to configure its sections.</p>
                </div>
                <div style={S.tableWrap}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={S.thead}>
                            <tr>
                                <th style={{ ...S.th, width: '40%' }}>Section</th>
                                <th style={{ ...S.th, width: '60%' }}>Featured Advisor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tempModules.filter(mod => mod.sections?.length > 0).map(mod => {
                                const isOpen = !!openAccordions[mod._id];
                                return (
                                    <React.Fragment key={mod._id}>
                                        <tr style={S.modRow} onClick={() => toggleAccordion(mod._id)}>
                                            <td colSpan={2} style={S.modTd}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={S.modAccent} />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={S.modName}>{mod.moduleName}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                                            {mod.moduleKey && <span style={S.modKey}>{mod.moduleKey}</span>}
                                                            <span style={S.modMeta}>{mod.sections?.length || 0} section{mod.sections?.length !== 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                    {isOpen
                                                        ? <KeyboardArrowUp style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                        : <KeyboardArrowDown style={{ color: '#4A67FF', fontSize: 20, flexShrink: 0 }} />
                                                    }
                                                </div>
                                            </td>
                                        </tr>

                                        {isOpen && (mod.sections || []).map(sec => (
                                            <tr key={`${mod._id}-${sec.sectionNo}`} style={S.secRow}>
                                                <td style={S.secTd}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={S.secNo}>{sec.sectionNo}</span>
                                                        <span style={S.secName}>{sec.sectionName}</span>
                                                    </div>
                                                </td>
                                                <td style={S.advisorTd}>
                                                    <Select
                                                        placeholder="Select advisor…"
                                                        styles={selectStyles}
                                                        formatOptionLabel={formatOptionLabel}
                                                        value={getSelectedAdvisor(sec.sectionNo, mod._id)}
                                                        options={advisorsData}
                                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                        menuPosition="fixed"
                                                        onChange={advisor =>
                                                            advisor && handleAllocation({
                                                                advisorId: advisor._id,
                                                                sectionNo: sec.sectionNo,
                                                                moduleId: mod._id,
                                                            })
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TempFeaturedBusinessCards;
